import {
  TeethSelect,
  validateSchema,
  type ValidationResult,
} from "@jasper/forms";
import { Stack } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { z } from "zod";

import type { ConfigurableProduct, ProductStepProps } from "../types";
import { useStepBulkGroup } from "../../hooks/useStepBulkGroup";
import {
  bulkTeethSchema,
  uniqueTeethSchema,
  type ItemType,
} from "@jasper/client-models";
import { useTranslation } from "react-i18next";
import {
  useOrderConfiguration,
  type UpdateProductArgs,
} from "@jasper/use-cases";
import { notifications } from "@mantine/notifications";
import { useTeethSelectionSubmission } from "./useTeethSelectionSubmission";
import { useRegisterStepSave } from "../useRegisterStepSave";
import { defaultApplyTeethSelection } from "./defaultApplyTeethSelection";
import { useTeethSelectionDescription } from "./useTeethSelectionDescription";

export type ToothStepProps = ProductStepProps & {
  applyTeethSelection?: (args: {
    teeth: number[];
    product: ConfigurableProduct;
    itemType: ItemType;
  }) => Promise<UpdateProductArgs> | UpdateProductArgs;
};

export const ToothStep = ({
  stepKey,
  product,
  orderId,
  itemType,
  bulkGroup,
  bulk,
  applyTeethSelection = defaultApplyTeethSelection,
  registerStepSave,
}: ToothStepProps) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });
  const describeTeethSelection = useTeethSelectionDescription();

  const { updateProduct } = useOrderConfiguration({
    orderId,
  });

  const isBulkSelection = Boolean(bulk?.isSupported);

  const { isBulkMode, groupTeeth } = useStepBulkGroup(product, bulkGroup);

  const currentTeeth = isBulkSelection ? groupTeeth : product.teeth;

  const submitTeethSelection = useTeethSelectionSubmission({
    currentGroupedTeeth: currentTeeth.map(tooth => [tooth]),
    isBulkSelection,
    isBulkMode,
    bulk,
  });

  const initialTeeth =
    isBulkSelection && bulk?.preselectedTeeth?.length
      ? bulk.preselectedTeeth
      : currentTeeth;

  const defaultValues = useMemo(
    () => ({
      product: {
        teeth: [...initialTeeth],
      },
    }),
    [initialTeeth]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await submitTeethSelection(
          value.product.teeth.map(tooth => [tooth]),
          applyTeethSelection,
          async () => {
            const updatedProduct = await applyTeethSelection({
              teeth: value.product.teeth,
              product,
              itemType,
            });

            await updateProduct(updatedProduct);
          }
        );
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const handleToothClick = ({
    toothId,
    selectedTeeth,
    setSelectedTeeth,
  }: {
    toothId: number;
    selectedTeeth: number[];
    setSelectedTeeth: (teeth: number[]) => void;
  }) => {
    if (!isBulkSelection) {
      setSelectedTeeth([toothId]);
      return;
    }

    if (selectedTeeth.includes(toothId)) {
      setSelectedTeeth(selectedTeeth.filter(id => id !== toothId));
    } else {
      setSelectedTeeth([...selectedTeeth, toothId]);
    }
  };

  const validateSelection = ({ value }: { value: { teeth: number[] } }) => {
    let result: ValidationResult<{ teeth: number[] }>;

    if (isBulkSelection) {
      result = validateSchema(
        z.object({
          teeth: bulkTeethSchema,
        }),
        value
      );
    } else {
      result = validateSchema(
        z.object({
          teeth: uniqueTeethSchema,
        }),
        value
      );
    }

    return result.success ? undefined : result.errors;
  };

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <form.Field
        name="product"
        listeners={{
          onChange: ({ fieldApi }) => {
            fieldApi.setErrorMap({
              onSubmit: undefined,
              onBlur: undefined,
            });
          },
        }}
        validators={{
          onChange: ({ value, fieldApi }) => {
            const errors = validateSelection({ value });

            if (!errors) {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            }

            return errors;
          },
          onSubmit: validateSelection,
          onBlur: validateSelection,
        }}
      >
        {field => {
          // Never display onChange errors, only onBlur and onSubmit.
          // Showing errors on onChange is too aggressive and can be confusing to the user.
          const formErrors =
            field.state.meta.errorMap.onBlur ??
            field.state.meta.errorMap.onSubmit;

          const errors =
            typeof formErrors?.teeth === "string"
              ? t(formErrors?.teeth, {
                  keyPrefix: "steps.teeth.errors",
                  defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                })
              : undefined;

          return (
            <TeethSelect
              onBlur={() => field.handleBlur()}
              teeth={field.state.value.teeth}
              error={errors}
              teethError={errors ? field.state.value.teeth : undefined}
              onClick={toothId => {
                handleToothClick({
                  toothId,
                  selectedTeeth: field.state.value.teeth,
                  setSelectedTeeth: teeth =>
                    field.setValue({ ...field.state.value, teeth }),
                });
              }}
              getTeethDescription={({ teeth }) =>
                describeTeethSelection(teeth, [
                  { type: itemType, count: teeth.length },
                ])
              }
            />
          );
        }}
      </form.Field>
    </Stack>
  );
};
